import React, { useCallback, useEffect, useState } from "react";

interface VirtualizedPagesProps {
  pages: string[];
  scrollRef: React.RefObject<HTMLDivElement | null>;
  slotHeight: number;
  overscan?: number;
  renderSlot: (pageId: string, index: number) => React.ReactNode;
}

/**
 * Renders only the page slots that intersect the visible scroll window,
 * keeping a tall spacer so scroll height matches the full document length.
 * Each slot is absolutely positioned at `index * slotHeight`.
 */
const VirtualizedPages: React.FC<VirtualizedPagesProps> = ({
  pages,
  scrollRef,
  slotHeight,
  overscan = 2,
  renderSlot,
}) => {
  const [range, setRange] = useState<{ start: number; end: number }>(() => ({
    start: 0,
    end: Math.min(pages.length, overscan + 3),
  }));

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollTop = el.scrollTop;
    const clientHeight = el.clientHeight || 800;
    const start = Math.max(0, Math.floor(scrollTop / slotHeight) - overscan);
    const end = Math.min(
      pages.length,
      Math.ceil((scrollTop + clientHeight) / slotHeight) + overscan,
    );
    setRange((prev) =>
      prev.start === start && prev.end === end ? prev : { start, end },
    );
  }, [pages.length, slotHeight, scrollRef, overscan]);

  useEffect(() => {
    update();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [update]);

  const slots: React.ReactNode[] = [];
  for (let i = range.start; i < range.end; i++) {
    const pageId = pages[i];
    if (!pageId) continue;
    slots.push(
      <div
        key={pageId}
        style={{
          position: "absolute",
          top: i * slotHeight,
          left: 0,
          right: 0,
          height: slotHeight,
        }}
      >
        {renderSlot(pageId, i)}
      </div>,
    );
  }

  return (
    <div
      style={{
        position: "relative",
        height: pages.length * slotHeight,
        width: "100%",
      }}
    >
      {slots}
    </div>
  );
};

export default VirtualizedPages;
